/**
 * When the judge's second reading of a quote earns a line of its own.
 *
 * The inspector and the Markdown report both print the judge's reading only where
 * it disagrees with the model's stance (D106). Printed on every disagreement, that
 * line ran on almost half of all sources: of 68 quoted sources from twenty real
 * checks, Jev and the check model disagreed on 32. Where Jev's probability was below
 * 0.85, the check model had the better reading more often than not. And Jev's
 * `disputes` was wrong all seven times: it read a quote that undercut a phenomenon as
 * disputing a claim that reports the undercutting (D366). So the line needs a
 * confident judge, and never stands on `disputes` alone.
 *
 * One predicate for both surfaces. The report is the inspector's rule in an
 * artefact nobody can open the inspector on, and two copies of one rule are how
 * they come to disagree.
 */

import type { WebSource } from './types'

/** How sure the judge has to be before its disagreement is printed. */
export const JUDGED_LINE_MIN = 0.85

/**
 * The judge's reading when it is worth a line, or null.
 *
 * `neither` is the judge's word for `context`, so the comparison is made in the
 * app's own vocabulary. Nothing here reaches a reading: `refutedIsEarned` never
 * reads `judged` at all.
 */
export function judgedDisagreement(
  s: Pick<WebSource, 'stance' | 'judged'>,
): NonNullable<WebSource['judged']> | null {
  const j = s.judged
  if (!j) return null
  const asStance = j.stance === 'neither' ? 'context' : j.stance
  if (asStance === s.stance) return null
  if (j.stance === 'disputes') return null
  if (j.probabilities[j.stance] < JUDGED_LINE_MIN) return null
  return j
}

/**
 * How many of the quotes a check counted as support the judge read, surely, as not
 * bearing on the claim at all (D378).
 *
 * The judge's one strong call. Across 76 real sources, where the check model said
 * `supports` and Jev said `neither` at 0.85 or more, Jev had the better reading six
 * times out of eight: a physical constant's value, a database's page title and a
 * conference's topic list, each counted as support. A per-source line caught that,
 * but only for a reader who opened the sources; the support count on the check's
 * headline went on counting them. So the count is said where the support is
 * counted, beside it and never in place of it: the tally, the reading and every
 * gate stay the check model's own (D106).
 *
 * Built on `judgedDisagreement`, so the bar is the line's bar and one rule decides
 * both. The model is the judge's own stamp, never assumed.
 */
export function offTopicSupport(
  sources: readonly Pick<WebSource, 'stance' | 'judged'>[],
): { n: number; model: string } | null {
  let n = 0
  let model: string | null = null
  for (const s of sources) {
    if (s.stance !== 'supports') continue
    const j = judgedDisagreement(s)
    if (j?.stance !== 'neither') continue
    n++
    model ??= j.model.name
  }
  return n && model ? { n, model } : null
}
